import React, { useEffect, useState } from "react";
import { Grid, Box, Typography, TextField, Stack, Link } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import {
    StyledTextField,
    StyledStartTimePicker,
    StyledEndTimePicker,
} from "../../utilities/styles/checkoutPage.style";
import { timezoneDateFormat } from "../../utilities/timezoneDateFormat";

interface EventInformationFormProps {
    eventName: string;
    eventDate: Date;
    expectedGuestCount: number;
    eventFieldStartTime: Date;
    eventFieldEndTime: Date;
    eventEnd: any;
    selectedDateError: string;
    eventTimeUpdatedMessage?: string;
    eventDateUpdatedMessage?: string; 
    showGuestCountError: boolean;
    guestCountError: string;
    openHoursList: any;
    organizationSettings: any;
    organizationOrdersOpenSchedule: any;
    minimumPickupDate: Date;
    estimatedReadyTimeMax: Date | null;
    classes: any;
    validator: any;
    onChange: (e: any, fieldName: string) => void;
    onStateChange: (updates: any) => void;
    showStorePolicy?: boolean;
}

const EventInformationForm: React.FC<EventInformationFormProps> = ({
    eventName,
    eventDate,
    expectedGuestCount,
    eventFieldStartTime,
    eventFieldEndTime,
    eventEnd,
    selectedDateError,
    showGuestCountError,
    guestCountError,
    openHoursList,
    organizationSettings,
    organizationOrdersOpenSchedule,
    minimumPickupDate,
    estimatedReadyTimeMax,
    classes,
    validator,
    onChange,
    onStateChange,
    showStorePolicy,
    eventDateUpdatedMessage,
    eventTimeUpdatedMessage
}) => {
    const [showStartTimeHelperText, setShowStartTimeHelperText] = useState(false);
    const [showEndTimeHelperText, setShowEndTimeHelperText] = useState(false);

    const dateFormat = timezoneDateFormat();

    const pickupTime = localStorage.getItem("requestedPickupDateTime");
    const eventEndDateTime = localStorage.getItem("eventEndDateTime");

    const toggleStartTimeHelperText = () => {
        if(pickupTime && new Date(pickupTime) < new Date()){
            setShowStartTimeHelperText(true)
        } else {
            setShowStartTimeHelperText(false);
        }
    }

     const toggleEndTimeHelperText = () => {
        if(eventEndDateTime && new Date(eventEndDateTime) < new Date()){
            setShowEndTimeHelperText(true)
        } else {
            setShowEndTimeHelperText(false);
        }
    }

    useEffect(() => {
        toggleStartTimeHelperText();
        toggleEndTimeHelperText();
    }, [eventDate, eventFieldStartTime, eventEndDateTime]);

    return (
        <>
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
            >
                <Typography
                    gutterBottom
                    sx={{
                        mt: 4,
                        mb: 2,
                        fontSize: { sm: "20px" },
                        fontWeight: 600,
                    }}
                >
                    Event Information
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "15px",
                    }}
                >
                    {showStorePolicy && (
                        <Box
                            onClick={() => {
                                onStateChange({ openPolicyModel: true });
                            }}
                            sx={{
                                mt: 4,
                                fontSize: { sm: "15px" },
                                fontWeight: 500,
                                color: "#0000ef",
                                cursor: "pointer",
                            }}
                        >
                            {"Store Policy"}
                        </Box>
                    )}
                    <Box
                        onClick={() => {
                            onStateChange({ openOpenHoursModel: true });
                        }}
                        sx={{
                            mt: 4,
                            fontSize: { sm: "15px" },
                            fontWeight: 500,
                            color: "#0000ef",
                            cursor: "pointer",
                        }}
                    >
                        {"Store Hours"}
                    </Box>
                    {organizationSettings?.checkoutLinkName &&
                        organizationSettings?.checkoutLinkURL && (
                            <Link
                                href={
                                    organizationSettings.checkoutLinkURL.startsWith("http")
                                        ? organizationSettings.checkoutLinkURL
                                        : `https://${organizationSettings.checkoutLinkURL}`
                                }
                                target="_blank"
                                sx={{
                                    mt: 4,
                                    fontSize: { sm: "15px" },
                                    fontWeight: 500,
                                    color: "#0000ef",
                                    cursor: "pointer",
                                }}
                                underline="none"
                            >
                                {organizationSettings.checkoutLinkName}
                            </Link>
                        )}
                </Box>
            </Box>
            <Grid container spacing={2} direction="row">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid item xs={12} sm={12}>
                        <StyledTextField
                            id="eventName"
                            label="Event Name (Optional)"
                            className={`${classes.marginBottom5} ${classes.fullWidth}`}
                            value={eventName}
                            variant="outlined"
                            onChange={(e: any) => onChange(e, "eventName")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DatePicker
                            label="Date Of Event"
                            value={eventDate}
                            onChange={(newValue) =>
                                onStateChange({ eventDate: newValue })
                            }
                            disablePast
                            format={dateFormat}
                            shouldDisableDate={(date) => {
                                const formattedDate = moment(date).format("YYYY-MM-DD");
                                const dayOfWeek = moment(date).format("dddd").toLowerCase();
                                const daySettings = organizationOrdersOpenSchedule
                                    ? organizationOrdersOpenSchedule[dayOfWeek]
                                    : null;
                                const isHoliday =
                                    organizationSettings?.ordersHolidays?.some(
                                        (holiday: any) =>
                                            moment(holiday.holidayDate).format("YYYY-MM-DD") ===
                                            formattedDate && holiday.closedAllDay
                                    );
                                const isClosedAllDay = daySettings && daySettings.closedAllDay;

                                return isHoliday || isClosedAllDay;
                            }}
                            minDate={
                                minimumPickupDate
                                    ? minimumPickupDate
                                    : estimatedReadyTimeMax
                                        ? estimatedReadyTimeMax
                                        : new Date()
                            }
                            slots={{
                                textField: TextField,
                            }}
                            slotProps={{
                                calendarHeader: {
                                    sx: {
                                        position: "relative",
                                        "& .MuiPickersArrowSwitcher-root": {
                                            width: 0,
                                        },
                                        "& .MuiPickersCalendarHeader-labelContainer": {
                                            mx: "auto",
                                        },
                                        "& .MuiIconButton-edgeEnd": {
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                        },
                                        "& .MuiIconButton-edgeStart": {
                                            position: "absolute",
                                            right: 0,
                                            top: 0,
                                            bottom: 0,
                                        },
                                        "& .MuiPickersCalendarHeader-switchViewIcon": {
                                            display: "none",
                                        },
                                    },
                                },
                                openPickerButton: {
                                    sx: {
                                        "& .MuiSvgIcon-root": {
                                            height: "26px",
                                            width: "26px",
                                            color: "#000",
                                        },
                                    },
                                },
                                textField: {
                                    variant: "outlined",
                                    required: true,
                                    id: "dateOfEvent",
                                    name: "dateOfEvent",
                                    fullWidth: true,
                                    InputProps: {
                                        sx: {
                                            borderRadius: "8px",
                                            "&.MuiOutlinedInput-root": {
                                                fontSize: "18px",
                                            },
                                        },
                                    },
                                    InputLabelProps: {
                                        sx: {
                                            "&.MuiInputLabel-root": {
                                                fontSize: "18px",
                                            },
                                        },
                                    },
                                },
                            }}
                        />
                         {eventDateUpdatedMessage  && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {eventDateUpdatedMessage}
                            </Typography>
                        )}
                        <div className={classes.selectedDateErrorMessage}>
                            {selectedDateError}
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <StyledTextField
                            name="expectedGuestCount"
                            type="number"
                            className={classes.marginBottom5}
                            required
                            id="expectedGuestCount"
                            value={expectedGuestCount}
                            onChange={(e) => {
                                if (
                                    e.target.value === "" ||
                                    (e.target.value.length <= 6 && /^\d*$/.test(e.target.value))
                                ) {
                                    onChange(e, "expectedGuestCount");
                                }
                            }}
                            label="Expected Guest Count"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            variant="outlined"
                            inputProps={{
                                style: { textAlign: "left" },
                                maxLength: 6,
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                            }}
                            error={showGuestCountError}
                            helperText={showGuestCountError ? guestCountError : ""}
                            sx={
                                showGuestCountError
                                    ? {
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "red",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "red",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "red",
                                            },
                                        },
                                        "& .MuiFormHelperText-root": {
                                            color: "red",
                                            marginTop: "3px",
                                        },
                                    }
                                    : {}
                            }
                        />
                        {validator.message(
                            "expected guest count",
                            expectedGuestCount,
                            "required|noSpecialChar"
                        )}
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <div className={classes.selectedDateErrorMessage}>
                            {selectedDateError}
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Stack direction="row" gap={2}>
                            <Grid item xs={12} sm={6}>
                               <StyledStartTimePicker
                                    name="newstarttime"
                                    label="Event Start Time"
                                    minTime={openHoursList?.openFrom}
                                    maxTime={openHoursList?.openTo}
                                    dateTime={eventFieldStartTime}
                                    onChange={(value: any) => {
                                    const startTime = new Date(value);
                                    const oneHourAfterStart = new Date(
                                        startTime.getTime() + 60 * 60 * 1000
                                    );

                                    const maxEndTime = openHoursList?.openTo
                                        ? new Date(openHoursList.openTo)
                                        : null;

                                    let finalEndTime = oneHourAfterStart;

                                    if (maxEndTime && oneHourAfterStart > maxEndTime) {
                                        finalEndTime = maxEndTime;
                                    }
                                    onStateChange({
                                        eventFieldStartTime: startTime,
                                        eventFieldEndTime: finalEndTime,
                                    });

                                    localStorage.setItem("requestedPickupDateTime", startTime.toISOString());
                                    localStorage.setItem("eventEndDateTime", finalEndTime.toISOString());
                                }}
                                    sx={{ minWidth: "100%" }}
                                />
                                    {showStartTimeHelperText && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            Time was updated, please review                                                    
                                        </Typography>
                                )}
                                {eventTimeUpdatedMessage && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                        {eventTimeUpdatedMessage}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <StyledEndTimePicker
                                    name="newEndTime"
                                    label="Event End Time"
                                    minTime={openHoursList?.openFrom}
                                    maxTime={openHoursList?.openTo}
                                    dateTime={eventFieldEndTime}
                                    onChange={(value: any) => {
                                        onStateChange({
                                            eventFieldEndTime: value,
                                        });
                                    localStorage.setItem("eventEndDateTime", value)
                                    }}
                                    sx={{ minWidth: "100%" }}
                                />
                                 {showEndTimeHelperText && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            Time was updated, please review                                                    
                                        </Typography>
                                )}
                            </Grid>
                            {validator.message("event end time", eventEnd, "required")}
                        </Stack>
                    </Grid>
                </LocalizationProvider>
            </Grid>
        </>
    );
};

export default EventInformationForm;
